// API service for connecting to the shared backend

import { resolveTenantId } from '../config/tenants';
import { chatStorageAPI } from './chatStorage';

export interface Attachment {
  type: 'image' | 'pdf';
  mime_type: string;
  data: string; // base64 encoded
  filename: string;
  size: number;
}

interface Message {
  role: "user" | "assistant" | "system";
  content: string;
  attachments?: Attachment[];
  // Study ids behind an earlier answer, as the backend sent them (event study_refs).
  study_refs?: string;
  metadata?: {
    tool_calls?: Array<{
      function: string;
      arguments: Record<string, unknown>;
      result: string;
    }>;
  };
}

interface ChatRequest {
  messages: Message[];
  stream?: boolean;
  context_vars?: Record<string, unknown>;
  system_addition?: string;
  tenant?: string;  // Specialty chatbot (neuro, derma, …) — scopes studies + prompt
  // Chat trace fields: the backend stores one trace per turn and /save links it by turn_id.
  turn_id?: string;
  session_id?: string;
  source?: string;
  campaign?: string;
}

export interface TurnTrace {
  turnId: string;
  sessionId?: string | null;
  source?: string | null;
  campaign?: string | null;
}

interface StreamEvent {
  token?: string;
  error?: string;
  detail?: string;
}

export interface ToolCallEvent {
  type: 'tool_call';
  function: string;
  arguments: Record<string, unknown>;
  result: string;
}

// Sent once after the answer. The note goes back with this answer in later requests.
export interface StudyRefsEvent {
  type: 'study_refs';
  note: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export class ChatAPI {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async validatePracticeLink(slug: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/public/practices/${encodeURIComponent(slug)}`);
    if (!response.ok) {
      throw new Error(response.status === 404
        ? 'Dieser Praxislink ist nicht verfügbar.'
        : 'Der Praxislink konnte nicht geprüft werden. Bitte versuchen Sie es erneut.');
    }
  }

  /**
   * Send a chat message and receive a streaming response
   * @param messages - Array of chat messages
   * @param signal - Optional AbortSignal for cancellation
   * @param trace - Identifies this turn's trace on the backend
   * @returns AsyncGenerator that yields tokens (strings) and tool call events (ToolCallEvent)
   */
  async *streamChat(
    messages: Message[],
    signal?: AbortSignal,
    trace?: TurnTrace,
  ): AsyncGenerator<string | ToolCallEvent | StudyRefsEvent, void, unknown> {
    const request: ChatRequest = {
      messages,
      stream: true,
      tenant: resolveTenantId(),
      turn_id: trace?.turnId,
      session_id: trace?.sessionId || undefined,
      source: trace?.source || undefined,
      campaign: trace?.campaign || undefined,
    };

    try {
      const response = await fetch(`${this.baseUrl}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal, // Pass the abort signal to fetch
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';
      let currentEventType: string | null = null;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();

            if (currentEventType === 'done') {
              return;
            } else if (currentEventType === 'error') {
              // Error details should come in the next data line
              console.error('Stream error event received');
            }
            // tool_call events will be handled when data arrives
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();

            if (data === '{}') {
              currentEventType = null;
              continue; // Skip empty data
            }

            try {
              // Handle tool_call events
              if (currentEventType === 'tool_call') {
                const toolData = JSON.parse(data);
                yield {
                  type: 'tool_call',
                  function: toolData.function,
                  arguments: toolData.arguments,
                  result: toolData.result,
                } as ToolCallEvent;
                currentEventType = null;
                continue;
              }

              if (currentEventType === 'study_refs') {
                const refs = JSON.parse(data);
                if (typeof refs.note === 'string') {
                  yield { type: 'study_refs', note: refs.note } as StudyRefsEvent;
                }
                currentEventType = null;
                continue;
              }

              // Handle regular data events (tokens)
              const event: StreamEvent = JSON.parse(data);

              if (event.error) {
                console.error('Stream error:', event.error, event.detail);
                throw new Error(event.detail || event.error);
              }

              if (event.token) {
                yield event.token;
              }

              currentEventType = null;
            } catch (parseError) {
              // Re-throw intentional errors (from event.error check above)
              if (parseError instanceof Error && parseError.message) {
                throw parseError;
              }
              // Skip invalid JSON parsing errors
              console.warn('Failed to parse SSE data:', data);
              currentEventType = null;
            }
          }
        }
      }
    } catch (error) {
      // Check if this is an abort error
      if (error instanceof DOMException && error.name === 'AbortError') {
        return; // Don't throw for user-initiated cancellation
      }
      console.error('Chat stream error:', error);
      throw error;
    }
  }

  /**
   * Send a chat message and receive a non-streaming response (for fallback)
   */
  async sendChat(messages: Message[]): Promise<string> {
    const request: ChatRequest = {
      messages,
      stream: false,
      tenant: resolveTenantId(),
    };

    try {
      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.content;
    } catch (error) {
      console.error('Chat error:', error);
      throw error;
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/healthz`);
      return response.ok && (await response.text()) === 'ok';
    } catch {
      return false;
    }
  }

  /**
   * Transcribe audio to text using Gemini API
   * @param audioBlob - Audio blob from MediaRecorder
   * @returns Transcribed text
   * @throws Error if transcription fails
   */
  async transcribeAudio(audioBlob: Blob): Promise<string> {
    try {
      console.log('Transcribing audio blob:', {
        size: audioBlob.size,
        type: audioBlob.type,
      });

      // Convert blob to base64
      const base64Audio = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          console.log('FileReader result length:', base64?.length);
          console.log('FileReader result prefix:', base64?.substring(0, 50));

          // Remove data URL prefix (e.g., "data:audio/webm;base64,")
          const parts = base64.split(',');
          if (parts.length !== 2) {
            reject(new Error('Invalid base64 data format'));
            return;
          }

          const base64Data = parts[1];
          console.log('Base64 data length:', base64Data.length);

          if (!base64Data || base64Data.length < 100) {
            reject(new Error('Base64 data too short or empty'));
            return;
          }

          resolve(base64Data);
        };
        reader.onerror = () => reject(new Error('Failed to read audio blob'));
        reader.readAsDataURL(audioBlob);
      });

      // Send transcription request
      const requestPayload = {
        audio_data: base64Audio,
        mime_type: audioBlob.type || 'audio/webm',
      };

      console.log('Sending transcription request:', {
        url: `${this.baseUrl}/chat/transcribe`,
        mime_type: requestPayload.mime_type,
        audio_data_length: requestPayload.audio_data.length,
      });

      const response = await fetch(`${this.baseUrl}/chat/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      console.log('Transcription response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: `HTTP error! status: ${response.status}`,
        }));
        console.error('Transcription error response:', errorData);

        // Extract validation errors if present
        let errorMessage = 'Transkription fehlgeschlagen';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            const errors = errorData.detail.map((e: { loc: Array<string | number>; msg: string }) =>
              `${e.loc.join('.')}: ${e.msg}`
            ).join(', ');
            errorMessage = `Validierungsfehler: ${errors}`;
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data.text;
    } catch (error) {
      console.error('Transcription error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Fehler bei der Audio-Transkription', { cause: error });
    }
  }

  /**
   * Submit user feedback to ClickUp
   * @param feedback - User feedback text
   * @returns Success response with message
   */
  async submitFeedback(feedback: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        feedback: feedback,
        source: 'patient_chat',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to submit feedback' }));
      throw new Error(errorData.message || errorData.detail || 'Failed to submit feedback');
    }

    return response.json();
  }

  /**
   * Send a study inquiry email via Resend API
   * @param data - Email sending request data
   * @returns Response with success status
   */
  async sendEmail(data: {
    recipientEmail: string;
    patientEmail: string;
    patientName: string;
    subject: string;
    body: string;
    chatId?: string;
  }): Promise<{ success: boolean; message?: string; error?: string; email_id?: string }> {
    try {
      const payload = {
        recipient_email: data.recipientEmail,
        patient_email: data.patientEmail,
        patient_name: data.patientName,
        subject: data.subject,
        body: data.body,
        chat_id: data.chatId,
        session_id: chatStorageAPI.getSessionId() || undefined,
      };
      // Repeated clicks and network retries of the same inquiry share one send.
      const digest = await crypto.subtle.digest(
        'SHA-256', new TextEncoder().encode(JSON.stringify(payload)),
      );
      const requestId = Array.from(new Uint8Array(digest), (byte) =>
        byte.toString(16).padStart(2, '0'),
      ).join('');
      const response = await fetch(`${this.baseUrl}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...payload, request_id: requestId }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle specific error responses
        return {
          success: false,
          error: result.detail || result.error || 'Failed to send email',
        };
      }

      return result;
    } catch (error) {
      console.error('Email send error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error while sending email',
      };
    }
  }
}

// Export a default instance
export const chatAPI = new ChatAPI();
